import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';
import { Button } from '../ui/button';
import {
  ArrowRight, Play, Wifi, Shield, LayoutDashboard, Network, Search, Lock,
  TrendingUp, Activity, Globe, Layers, Eye, Gauge, AlertTriangle, Wallet,
  Percent, Clock, Container, Map, Brain, Database, ChevronDown, MessageCircle,
  CheckCircle, Zap, Server, Users, Satellite, Package, Cpu, BarChart3, MapPin, Receipt, Sparkles, Bell
} from 'lucide-react';
import {
  wifiHeroData, wifiFeaturesData, wifiCapabilitiesData, wifiProductsData, wifiStatsData,
  starlinkHeroData, starlinkFeaturesData, starlinkCapabilitiesData, starlinkStatsData,
  finopsHeroData, finopsFeaturesData, finopsCapabilitiesData, finopsStatsData, finopsBenefitsData,
  solutionLayersFAQData
} from '../../data/solutionLayersData';

const iconMap = {
  LayoutDashboard, Shield, Wifi, Network, Search, Lock, TrendingUp, Activity,
  Globe, Layers, Eye, Gauge, AlertTriangle, Wallet, Percent, Clock, Container,
  Map, Brain, Database, Satellite, Package, Cpu, BarChart3, MapPin, Receipt, Sparkles, Bell, Users
};

// In-page Navigation Menu
const InPageMenu = ({ activeSection }) => {
  const sections = [
    { id: 'wifi', label: 'WiFi Administrado', icon: Wifi },
    { id: 'starlink', label: 'Starlink Management', icon: Satellite },
    { id: 'finops', label: 'FinOps', icon: Gauge }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100; // Account for fixed navbar
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="sticky top-20 z-40 bg-white/95 backdrop-blur-md shadow-sm border-b border-[#e3e4e6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-2 py-3 overflow-x-auto">
          {sections.map((section) => {
            const IconComponent = section.icon;
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                data-testid={`nav-${section.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-[#1297fd] text-white shadow-lg'
                    : 'bg-[#e3e4e6]/50 text-gray-700 hover:bg-[#8bcaf9]/30 hover:text-[#1297fd]'
                }`}
              >
                <IconComponent className="w-4 h-4" />
                {section.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// WiFi Hero Section
const WiFiHeroSection = () => (
  <section id="wifi" className="relative py-24 overflow-hidden bg-dark-ellipse">
    <div className="absolute inset-0 opacity-20">
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />
    </div>
    <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
    <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#8bcaf9]/30 rounded-full blur-3xl animate-pulse" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20">
            <Wifi className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">{wifiHeroData.tagline}</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
            {wifiHeroData.title}
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-xl leading-relaxed">
            {wifiHeroData.subtitle}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contacto?source=demo">
              <Button size="lg" className="bg-white hover:bg-[#e3e4e6] text-[#1297fd] font-semibold px-8 py-6 rounded-full transition-all hover:scale-105 group">
                {wifiHeroData.cta}
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Ubiquiti Visual */}
        <div className="relative hidden lg:block">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20">
            <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                    <Wifi className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white font-semibold">UniFi Controller</span>
                </div>
                <span className="text-green-400 text-sm">Online</span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                {[{ label: 'Dispositivos', value: '247' }, { label: 'Clientes', value: '1,892' }, { label: 'Uptime', value: '99.98%' }, { label: 'Throughput', value: '4.2 Gbps' }].map((stat) => (
                  <div key={stat.label} className="bg-white/5 rounded-xl p-3 text-center">
                    <p className="text-white font-bold">{stat.value}</p>
                    <p className="text-[#8bcaf9] text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {['Cloud Gateway Ultra - Activo', 'U7 Pro AP - 45 clientes', 'U6 Enterprise - 67 clientes'].map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <span className="text-white/80 text-sm">{item}</span>
                    <div className="w-2 h-2 bg-green-400 rounded-full" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
        {wifiStatsData.map((stat, idx) => (
          <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/15 transition-all">
            <p className="text-3xl font-bold text-white mb-2">{stat.number}</p>
            <p className="text-white/70 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// WiFi Features Section
const WiFiFeaturesSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Características WiFi
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Potencia tu oferta con{' '}
          <span className="text-[#1297fd]">WiFi empresarial</span>
        </h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {wifiFeaturesData.map((feature) => {
          const IconComponent = iconMap[feature.icon];
          return (
            <div key={feature.id} className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all hover:shadow-xl hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-[#c3c3c5] text-sm leading-relaxed">{feature.description}</p>
            </div>
          );
        })}
      </div>

      {/* Capabilities Grid */}
      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {wifiCapabilitiesData.map((cap) => {
          const IconComponent = iconMap[cap.icon];
          return (
            <div key={cap.id} className="flex gap-4 p-4 rounded-xl hover:bg-[#e3e4e6]/30 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-[#8bcaf9]/20 flex items-center justify-center flex-shrink-0">
                {IconComponent && <IconComponent className="w-5 h-5 text-[#1297fd]" />}
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">{cap.title}</h4>
                <p className="text-[#c3c3c5] text-sm">{cap.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Ubiquiti Products Section
const UbiquitiProductsSection = () => (
  <section className="py-24 bg-[#e3e4e6]/30">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#1297fd]/10 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Hardware Ubiquiti
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Equipos UniFi{' '}
          <span className="text-[#1297fd]">recomendados</span>
        </h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {wifiProductsData.map((product) => (
          <div key={product.id} className="group bg-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all hover:shadow-xl hover:-translate-y-2">
            <div className="h-32 bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl mb-4 flex items-center justify-center overflow-hidden">
              <div className="w-20 h-20 rounded-full bg-[#1297fd]/10 flex items-center justify-center">
                <Server className="w-10 h-10 text-[#1297fd]" />
              </div>
            </div>
            <h3 className="font-bold text-gray-900 mb-2 group-hover:text-[#1297fd] transition-colors">{product.name}</h3>
            <p className="text-[#c3c3c5] text-sm mb-4">{product.description}</p>
            <div className="flex flex-wrap gap-2">
              {product.specs.map((spec) => (
                <span key={spec} className="px-2 py-1 bg-[#8bcaf9]/20 text-[#1297fd] text-xs rounded-lg font-medium">
                  {spec}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Starlink Hero Section
const StarlinkHeroSection = () => (
  <section id="starlink" className="relative py-24 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
    <div className="absolute inset-0 opacity-30">
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 50% 50%, rgba(18,151,253,0.15) 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} />
    </div>
    <div className="absolute top-10 right-20 w-96 h-96 bg-[#1297fd]/20 rounded-full blur-3xl animate-pulse" />
    <div className="absolute bottom-10 left-20 w-72 h-72 bg-[#40aaf8]/20 rounded-full blur-3xl animate-pulse" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20">
            <Satellite className="w-4 h-4 text-[#40aaf8]" />
            <span className="text-white text-sm font-medium">{starlinkHeroData.tagline}</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
            {starlinkHeroData.title}
          </h2>
          <p className="text-lg text-white/70 mb-8 max-w-xl leading-relaxed">
            {starlinkHeroData.subtitle}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contacto?source=demo">
              <Button size="lg" className="text-white font-semibold px-8 py-6 rounded-full transition-all hover:scale-105 group" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                {starlinkHeroData.cta}
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Starlink Dashboard Visual */}
        <div className="relative hidden lg:block">
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10">
            <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#1297fd]">
                    <Satellite className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white font-semibold">Starlink Manager</span>
                </div>
                <span className="text-green-400 text-sm flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  Live
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                {[{ label: 'Terminales', value: '1,247' }, { label: 'Online', value: '1,198' }, { label: 'Clientes', value: '89' }, { label: 'Bolsas activas', value: '312' }].map((stat) => (
                  <div key={stat.label} className="bg-white/5 rounded-xl p-3 text-center">
                    <p className="text-white font-bold text-lg">{stat.value}</p>
                    <p className="text-[#8bcaf9] text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {[
                  { name: 'Empresa A - 45 terminales', status: 'Online' },
                  { name: 'Empresa B - 128 terminales', status: 'Online' },
                  { name: 'Empresa C - 23 terminales', status: '2 en mantenimiento' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <span className="text-white/80 text-sm">{item.name}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${item.status === 'Online' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {item.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
        {starlinkStatsData.map((stat, idx) => (
          <div key={idx} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/10 transition-all">
            <p className="text-3xl font-bold text-white mb-2">{stat.number}</p>
            <p className="text-white/60 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Starlink Features Section
const StarlinkFeaturesSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-gray-900/10 text-gray-900 rounded-full text-sm font-medium mb-4">
          Características Starlink
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Gestión completa de{' '}
          <span className="text-[#1297fd]">flotas Starlink</span>
        </h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {starlinkFeaturesData.map((feature) => {
          const IconComponent = iconMap[feature.icon];
          return (
            <div key={feature.id} className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-gray-900 transition-all hover:shadow-xl hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform bg-gray-900">
                {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-[#c3c3c5] text-sm leading-relaxed">{feature.description}</p>
            </div>
          );
        })}
      </div>

      {/* Capabilities Grid */}
      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {starlinkCapabilitiesData.map((cap) => {
          const IconComponent = iconMap[cap.icon];
          return (
            <div key={cap.id} className="flex gap-4 p-4 rounded-xl hover:bg-gray-100 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-gray-900/10 flex items-center justify-center flex-shrink-0">
                {IconComponent && <IconComponent className="w-5 h-5 text-gray-900" />}
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">{cap.title}</h4>
                <p className="text-[#c3c3c5] text-sm">{cap.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// FinOps Hero Section
const FinOpsHeroSection = () => (
  <section id="finops" className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-[#8bcaf9]/20 rounded-full px-4 py-2 mb-6">
            <Gauge className="w-4 h-4 text-[#1297fd]" />
            <span className="text-[#1297fd] text-sm font-medium">{finopsHeroData.tagline}</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-6">
            {finopsHeroData.title.split('cloud').map((part, idx) => (
              <span key={idx}>
                {idx > 0 && <span className="text-[#1297fd]">cloud</span>}
                {part}
              </span>
            ))}
          </h2>
          <p className="text-lg text-[#c3c3c5] mb-8 max-w-xl leading-relaxed">
            {finopsHeroData.subtitle}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contacto?source=demo">
              <Button size="lg" className="text-white font-semibold px-8 py-6 rounded-full transition-all hover:scale-105 group" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                {finopsHeroData.cta}
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>

        {/* FinOps Dashboard Visual */}
        <div className="relative">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <span className="text-white font-semibold">FinOps Dashboard</span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full" />
                <span className="text-green-400 text-sm">Sincronizado</span>
              </div>
            </div>
            
            {/* Cloud Providers */}
            <div className="flex gap-2 mb-6">
              {['AWS', 'Azure', 'GCP', 'K8s'].map((cloud) => (
                <span key={cloud} className="px-3 py-1 bg-white/10 rounded-lg text-white text-xs">{cloud}</span>
              ))}
            </div>

            {/* Cost Breakdown */}
            <div className="bg-white/5 rounded-xl p-4 mb-4">
              <div className="flex justify-between items-center mb-3">
                <span className="text-white text-sm">Gasto Mensual</span>
                <span className="text-green-400 text-sm">-35% vs anterior</span>
              </div>
              <p className="text-white font-bold text-3xl mb-2">$47,892</p>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full w-3/4 rounded-full" style={{ background: 'linear-gradient(to right, #1297fd, #40aaf8)' }} />
              </div>
            </div>

            {/* Savings */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-500/20 rounded-xl p-4">
                <p className="text-green-400 font-bold text-xl">$24,500</p>
                <p className="text-white/70 text-xs">Ahorro este mes</p>
              </div>
              <div className="bg-[#1297fd]/20 rounded-xl p-4">
                <p className="text-[#40aaf8] font-bold text-xl">127</p>
                <p className="text-white/70 text-xs">Recomendaciones</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
        {finopsStatsData.map((stat, idx) => (
          <div key={idx} className="bg-[#e3e4e6]/30 rounded-2xl p-6 text-center hover:bg-[#8bcaf9]/10 transition-all">
            <p className="text-3xl font-bold text-[#1297fd] mb-2">{stat.number}</p>
            <p className="text-[#c3c3c5] text-sm">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// FinOps Features Section
const FinOpsFeaturesSection = () => (
  <section className="py-24 bg-dark-ellipse">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-4 border border-white/20">
          Capacidades FinOps
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
          Control total de costes cloud
        </h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {finopsFeaturesData.map((feature) => {
          const IconComponent = iconMap[feature.icon];
          return (
            <div key={feature.id} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/15 transition-all">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
              </div>
              <h3 className="font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-white/70 text-sm leading-relaxed">{feature.description}</p>
            </div>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {finopsCapabilitiesData.map((cap) => {
          const IconComponent = iconMap[cap.icon];
          return (
            <div key={cap.id} className="flex gap-3 bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                {IconComponent && <IconComponent className="w-4 h-4 text-white" />}
              </div>
              <div>
                <h4 className="font-semibold text-white text-sm mb-1">{cap.title}</h4>
                <p className="text-white/60 text-xs">{cap.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// FinOps Benefits Section
const FinOpsBenefitsSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Beneficios
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          FinOps para todo tu{' '}
          <span className="text-[#1297fd]">ecosistema</span>
        </h2>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {finopsBenefitsData.map((benefit, idx) => (
          <div key={benefit.id} className={`rounded-3xl p-8 ${idx === 1 ? 'text-white shadow-xl' : 'bg-white border border-[#e3e4e6]'}`} style={idx === 1 ? { background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' } : {}}>
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${idx === 1 ? 'bg-white/20' : 'bg-[#8bcaf9]/20'}`}>
              <Users className={`w-7 h-7 ${idx === 1 ? 'text-white' : 'text-[#1297fd]'}`} />
            </div>
            <h3 className={`text-xl font-bold mb-3 ${idx === 1 ? 'text-white' : 'text-gray-900'}`}>{benefit.title}</h3>
            <p className={`mb-6 ${idx === 1 ? 'text-white/80' : 'text-[#c3c3c5]'}`}>{benefit.description}</p>
            <ul className="space-y-2">
              {benefit.features.map((feat) => (
                <li key={feat} className="flex items-center gap-2">
                  <CheckCircle className={`w-4 h-4 ${idx === 1 ? 'text-white' : 'text-[#1297fd]'}`} />
                  <span className={`text-sm ${idx === 1 ? 'text-white/90' : 'text-gray-700'}`}>{feat}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <section className="py-24 bg-[#e3e4e6]/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">FAQ</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Preguntas frecuentes</h2>
        </div>
        <div className="space-y-4">
          {solutionLayersFAQData.map((item, idx) => (
            <div key={idx} className={`rounded-2xl border-2 transition-all overflow-hidden ${openIndex === idx ? 'border-[#8bcaf9] bg-white shadow-lg' : 'border-[#e3e4e6] bg-white'}`}>
              <button onClick={() => setOpenIndex(openIndex === idx ? -1 : idx)} className="w-full p-6 text-left flex items-center justify-between gap-4">
                <span className={`font-semibold text-lg ${openIndex === idx ? 'text-[#1297fd]' : 'text-gray-900'}`}>{item.question}</span>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${openIndex === idx ? 'rotate-180' : 'bg-[#e3e4e6]'}`} style={openIndex === idx ? { background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' } : {}}>
                  <ChevronDown className={`w-5 h-5 ${openIndex === idx ? 'text-white' : 'text-gray-600'}`} />
                </div>
              </button>
              <div className={`overflow-hidden transition-all ${openIndex === idx ? 'max-h-96' : 'max-h-0'}`}>
                <div className="px-6 pb-6"><p className="text-[#c3c3c5] leading-relaxed">{item.answer}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section className="py-24 bg-dark-ellipse">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Potencia tu negocio con nuevas capas de solución</h2>
      <p className="text-lg text-white/80 mb-10">WiFi administrado y FinOps como servicios de valor agregado para diferenciarte en el mercado.</p>
      <div className="flex flex-wrap justify-center gap-4">
        <Link to="/contacto?source=demo">
          <Button size="lg" className="bg-white hover:bg-[#e3e4e6] text-[#1297fd] font-semibold px-10 py-6 rounded-full transition-all hover:scale-105 group">
            Solicitar demo
            <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Button>
        </Link>
        <Link to="/contacto?source=ventas">
          <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-6 rounded-full transition-all hover:scale-105 bg-transparent">
            Contactar ventas
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

// Main Page Component
const SolutionLayersPage = () => {
  const [activeSection, setActiveSection] = useState('wifi');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['wifi', 'starlink', 'finops'];
      const scrollPosition = window.scrollY + 150;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Soluciones Avanzadas · WiFi, Starlink y FinOps"
        description="Capas avanzadas de la plataforma: gestión WiFi multi-sede, Starlink con observabilidad, provisionamiento de clientes, bolsas y paquetes de datos y control de consumos, y FinOps para optimizar el gasto cloud."
        keywords="WiFi gestionado, Starlink, observabilidad Starlink, provisionamiento de clientes, bolsas de datos, paquetes de datos, consumos, FinOps, conectividad remota"
        canonical="/soluciones-avanzadas"
      />
      <Navbar />
      
      {/* Page Header */}
      <section className="pt-32 pb-16 bg-dark-ellipse">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-4 border border-white/20">
            Nuevas Capas de Solución
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Potencia tu negocio con servicios de valor agregado
          </h1>
          <p className="text-lg text-white/80 max-w-3xl mx-auto">
            Expande tu oferta con WiFi administrado, gestión Starlink y plataforma FinOps transversal para optimización de costes cloud.
          </p>
        </div>
      </section>

      {/* In-Page Navigation */}
      <InPageMenu activeSection={activeSection} />

      <WiFiHeroSection />
      <WiFiFeaturesSection />
      <UbiquitiProductsSection />
      <StarlinkHeroSection />
      <StarlinkFeaturesSection />
      <FinOpsHeroSection />
      <FinOpsFeaturesSection />
      <FinOpsBenefitsSection />
      <FAQSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default SolutionLayersPage;
